import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Trash2, Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const DELETE_PASSWORD = "Om@8888";

/**
 * Password-protected delete confirmation dialog
 * 
 * Usage:
 * const [showDeleteDialog, setShowDeleteDialog] = useState(false);
 * const [deleteTarget, setDeleteTarget] = useState(null);
 * 
 * // To trigger deletion:
 * setDeleteTarget({ id: item.id, name: item.name });
 * setShowDeleteDialog(true);
 * 
 * // In JSX:
 * <DeleteConfirmDialog
 *   open={showDeleteDialog}
 *   onOpenChange={setShowDeleteDialog}
 *   title="Delete Product"
 *   description={`Are you sure you want to delete "${deleteTarget?.name}"?`}
 *   onConfirm={() => handleDelete(deleteTarget.id)}
 * />
 */
const DeleteConfirmDialog = ({
  open,
  onOpenChange,
  title = "Confirm Deletion",
  description = "This action cannot be undone.",
  itemName = "",
  onConfirm,
  loading = false
}) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    setPassword("");
    setError("");
    setShowPassword(false);
    onOpenChange(false);
  };

  const handleConfirm = () => {
    if (password !== DELETE_PASSWORD) {
      setError("Incorrect password. Deletion not authorized.");
      toast.error("Incorrect deletion password");
      return;
    }

    setError("");
    onConfirm();
    handleClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && password) {
      handleConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {description}
            {itemName && (
              <span className="block mt-2 font-medium text-slate-800">
                &quot;{itemName}&quot;
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Trash2 className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-red-700">
                <p className="font-medium">Warning: This action cannot be undone!</p>
                <p className="mt-1 text-red-600">
                  Enter the deletion password to proceed.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="delete-password" className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-slate-500" />
              Deletion Password
            </Label>
            <div className="relative">
              <Input
                id="delete-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                onKeyDown={handleKeyDown}
                placeholder="Enter password to confirm deletion"
                className={error ? "border-red-500 pr-10" : "pr-10"}
                autoFocus
                data-testid="delete-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {error && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {error}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!password || loading}
            className="gap-2"
            data-testid="confirm-delete-btn"
          >
            <Trash2 className="w-4 h-4" />
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteConfirmDialog;

// Export the password check function for use in other components
export const verifyDeletePassword = (password) => {
  return password === DELETE_PASSWORD;
};
